import { Fragment, useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronUp, KeyRound, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FreeAccess } from "@/pages/admin/FreeAccess";

const COLORS = ["#4F46E5", "#8B5CF6", "#F59E0B", "#10B981", "#EC4899", "#0EA5E9", "#6366F1", "#14B8A6", "#F97316", "#84CC16", "#A855F7", "#64748B"];
const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["all", "All time"]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "—");
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 60) return `${Math.max(m, 0)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

function Section({ title, children, right }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2"><h3 className="font-display text-lg font-bold">{title}</h3>{right}</div>
      {children}
    </div>
  );
}

export function FeatureUsage() {
  const [d, setD] = useState(null);
  const [range, setRange] = useState("30d");
  useEffect(() => { api.get(`/admin/features?range=${range}`).then(({ data }) => setD(data)).catch(() => {}); }, [range]);
  const chips = (
    <div className="flex flex-wrap gap-1.5" data-testid="features-range">
      {RANGES.map(([k, l]) => <button key={k} onClick={() => setRange(k)} data-testid={`features-range-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${range === k ? "bg-slate-900 text-white" : "border border-border hover:bg-secondary"}`}>{l}</button>)}
    </div>
  );
  return (
    <Section title="Most used features" right={chips}>
      {!d ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-indigo-600" /> : d.features.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="features-empty">No feature activity in this period.</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3" data-testid="features-chart">
            <ResponsiveContainer width="100%" height={Math.max(180, d.features.length * 34)}>
              <BarChart data={d.features} layout="vertical" margin={{ left: 8, right: 24 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="feature" width={120} tick={{ fontSize: 12, fontWeight: 600 }} />
                <Tooltip formatter={(v, n) => [v, n === "events" ? "uses" : n]} cursor={{ fill: "rgba(79,70,229,0.06)" }} />
                <Bar dataKey="events" radius={[0, 8, 8, 0]} label={{ position: "right", fontSize: 11, fontWeight: 700 }}>
                  {d.features.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 lg:col-span-2" data-testid="features-list">
            <div className="mb-2 text-xs text-muted-foreground">{d.total_events.toLocaleString("en-IN")} feature uses in this period</div>
            {d.features.map((f, i) => (
              <div key={f.feature} data-testid={`feature-row-${i}`} className="flex items-center justify-between rounded-xl px-3 py-2 text-sm odd:bg-secondary/60">
                <span className="flex items-center gap-2 font-semibold"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{i === 0 && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">Top</span>}{f.feature}</span>
                <span className="font-mono text-xs text-muted-foreground">{f.events} uses · {f.users} users · {f.share}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function SetPassword({ user, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = () => {
    const words = ["Nxt", "Career", "Rocket", "Focus", "Level", "Quest"];
    setPw(`${words[Math.floor(Math.random() * words.length)]}${Math.floor(1000 + Math.random() * 9000)}!`);
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/users/${user.id}/set-password`, { new_password: pw }); toast.success(data.message); onDone?.(pw); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3 text-sm" data-testid={`set-password-${user.id}`}>
      <div className="flex items-center gap-2 font-bold"><KeyRound className="h-4 w-4" /> Set a password for this user</div>
      <p className="mt-1 text-xs text-muted-foreground">Passwords are stored hashed (bcrypt) and can never be viewed — not even by you. Set a new one here and share it with the user securely; they can change it later.{user.via === "google" && " This account signed up with Google — setting a password also enables email login."}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+, uppercase, number)" data-testid={`set-password-input-${user.id}`} className="min-w-[220px] flex-1 rounded-xl border border-input bg-card px-3 py-2 font-mono text-xs outline-none focus:border-indigo-500" />
        <button onClick={gen} data-testid={`gen-password-${user.id}`} className="rounded-full border border-border bg-white px-3 py-2 text-xs font-bold hover:bg-secondary">Generate</button>
        <button onClick={save} disabled={busy || pw.length < 8} data-testid={`save-password-${user.id}`} className="rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-600 disabled:opacity-50">{busy ? "Saving…" : "Set password"}</button>
      </div>
    </div>
  );
}

function UserDetail({ id }) {
  const { user: me } = useAuth();
  const [d, setD] = useState(null);
  const [setPw, setSetPw] = useState(null);
  const load = () => api.get(`/admin/users/${id}`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="p-4"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></div>;
  const max = Math.max(...d.features.map((f) => f.events), 1);
  const stat = (l, v) => <div className="rounded-xl bg-secondary px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{l}</div><div className="font-display text-base font-bold">{v}</div></div>;
  return (
    <div className="space-y-4 border-t border-border bg-secondary/30 p-4" data-testid={`user-detail-${id}`}>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {stat("Signed up", fmtDate(d.created_at))}{stat("Last active", ago(d.last_active))}{stat("Total events", d.events)}
        {stat("XP", d.xp)}{stat("Missions", d.missions_completed)}{stat("Login via", d.via)}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground"><Activity className="h-3.5 w-3.5" /> Feature usage</div>
          {d.features.length === 0 ? <p className="text-sm text-muted-foreground">No feature activity yet.</p> : d.features.map((f, i) => (
            <div key={f.feature} className="mb-1.5 text-sm" data-testid={`user-feature-${i}`}>
              <div className="flex justify-between"><span className="font-semibold">{i === 0 && "★ "}{f.feature}</span><span className="font-mono text-xs text-muted-foreground">{f.events} · last {ago(f.last_used)}</span></div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-indigo-600" style={{ width: `${(f.events / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Recent activity</div>
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {d.recent_activity.map((e, i) => <div key={i} className="flex justify-between rounded-lg bg-card px-2.5 py-1.5 text-xs"><span className="font-mono">{e.name}</span><span className="text-muted-foreground">{new Date(e.ts).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>)}
            {d.recent_activity.length === 0 && <p className="text-sm text-muted-foreground">No events yet.</p>}
          </div>
        </div>
      </div>
      {d.subscription && <div className="text-xs text-muted-foreground">Subscription: <b>{d.subscription.plan}</b> · {d.subscription.status} · until {fmtDate(d.subscription.expires_at)}</div>}
      {me?.role === "super_admin" && (
        setPw ? <p className="rounded-2xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-800" data-testid={`password-set-${id}`}>Password set for {d.email}: <code className="select-all rounded bg-white px-1.5 py-0.5 font-mono">{setPw}</code> — copy it now; it won't be shown again.</p>
          : <SetPassword user={d} onDone={(pw) => { setSetPw(pw); load(); }} />
      )}
    </div>
  );
}

function UserTable() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [sort, setSort] = useState("created_at");
  const [open, setOpen] = useState(null);
  const load = () => api.get(`/admin/users?q=${encodeURIComponent(q)}&plan=${plan}&sort=${sort}`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, [plan, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name or email…"
          data-testid="admin-user-search" className="flex-1 rounded-xl border border-input bg-card px-4 py-2.5 outline-none focus:border-indigo-500" />
        <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="admin-plan-filter" className="rounded-xl border border-input bg-card px-3 py-2.5">
          <option value="">All plans</option><option value="free">Free</option><option value="plus">Plus</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="admin-user-sort" className="rounded-xl border border-input bg-card px-3 py-2.5">
          <option value="created_at">Newest first</option><option value="last_active">Recently active</option><option value="events">Most active</option>
        </select>
        <button onClick={load} data-testid="admin-user-search-btn" className="rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white hover:bg-indigo-700">Search</button>
      </div>
      <p className="text-xs text-muted-foreground">Click a row for full activity, feature breakdown and password tools. Passwords are hashed and cannot be displayed — super admins can set a new one.</p>
      {!d ? <Loader2 className="mx-auto my-12 h-8 w-8 animate-spin text-indigo-600" /> : (
        <div className="overflow-x-auto rounded-3xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-secondary text-left text-xs uppercase tracking-widest text-muted-foreground">
              {["User", "Plan", "Signed up", "Last active", "Top feature", "Activity", "Target", "Readiness", ""].map((h) => <th key={h} className="p-3 font-bold">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.users.map((u) => (
                <Fragment key={u.id}>
                  <tr onClick={() => setOpen(open === u.id ? null : u.id)} className={`cursor-pointer border-b border-border/50 last:border-0 hover:bg-secondary/50 ${open === u.id ? "bg-secondary/40" : ""}`} data-testid="admin-user-row">
                    <td className="p-3"><div className="font-semibold">{u.name}</div><div className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="font-mono" data-testid="admin-user-email">{u.email}</span><span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${u.via === "google" ? "bg-sky-100 text-sky-700" : "bg-secondary"}`}>{u.via}</span></div></td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${u.plan === "plus" ? "bg-indigo-100 text-indigo-700" : "bg-secondary"}`}>{u.plan}</span></td>
                    <td className="p-3 text-xs">{fmtDate(u.created_at)}</td>
                    <td className="p-3 text-xs" data-testid="admin-user-last-active">{ago(u.last_active)}</td>
                    <td className="p-3 text-xs font-semibold" data-testid="admin-user-top-feature">{u.top_feature || "—"}</td>
                    <td className="p-3 text-xs">{u.events} events · {u.feature_count} features</td>
                    <td className="p-3 text-xs">{u.target_career || "—"}</td><td className="p-3 text-xs">{u.readiness}%</td>
                    <td className="p-3 text-muted-foreground">{open === u.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</td>
                  </tr>
                  {open === u.id && <tr><td colSpan={9} className="p-0"><UserDetail id={u.id} /></td></tr>}
                </Fragment>
              ))}
            </tbody>
          </table>
          {d.users.length === 0 && <p className="p-8 text-center text-muted-foreground">No users found.</p>}
          <div className="p-3 text-xs text-muted-foreground">Total: {d.total} users</div>
        </div>
      )}
    </div>
  );
}

export default function UsersTab() {
  return (
    <div className="space-y-5">
      <FreeAccess />
      <FeatureUsage />
      <UserTable />
    </div>
  );
}
